import React from "react";
import { Link } from "react-router-dom";
import { Phone, MapPin, Mail, Clock, Shield, Lock, BadgeCheck } from "lucide-react";
import { BRAND } from "@/lib/brand";
import { PROVIDER_LABEL } from "@/lib/payments";
import ThumbtackBadge from "@/components/ThumbtackBadge";

export default function Footer() {
  return (
    <footer className="bg-[var(--brand-teal-deep)] text-white mt-24" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16 grid grid-cols-1 md:grid-cols-4 gap-10">
        <div className="md:col-span-1">
          <div className="flex items-center gap-3 mb-4">
            <img src={BRAND.logoUrl} alt="Logo" className="h-14 w-14 object-contain bg-white rounded-lg p-1" />
            <div>
              <div className="font-extrabold text-lg" style={{ fontFamily: "Outfit" }}>Top-Notch Pro</div>
              <div className="text-[10px] tracking-[0.18em] uppercase text-[var(--brand-gold)] font-bold">Home Care & Property</div>
            </div>
          </div>
          <p className="text-sm text-white/70 leading-relaxed">
            Elite property maintenance and residential cleaning serving Evansville, IN. Punctual, honest, and committed to a 5-star result every time.
          </p>
        </div>

        <div>
          <h4 className="font-bold mb-4 text-[var(--brand-gold)] uppercase text-xs tracking-widest" style={{ fontFamily: "Outfit" }}>Explore</h4>
          <ul className="space-y-2 text-sm text-white/80">
            <li><Link to="/" className="hover:text-[var(--brand-gold)] transition">Home</Link></li>
            <li><Link to="/services" className="hover:text-[var(--brand-gold)] transition">Services</Link></li>
            <li><Link to="/about" className="hover:text-[var(--brand-gold)] transition">About Us</Link></li>
            <li><Link to="/quote" className="hover:text-[var(--brand-gold)] transition">Instant Booking</Link></li>
            <li><Link to="/request-quote" className="hover:text-[var(--brand-gold)] transition">Custom Quote Request</Link></li>
            <li><Link to="/contact" className="hover:text-[var(--brand-gold)] transition">Contact</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="font-bold mb-4 text-[var(--brand-gold)] uppercase text-xs tracking-widest" style={{ fontFamily: "Outfit" }}>Contact</h4>
          <ul className="space-y-3 text-sm text-white/80">
            <li className="flex items-start gap-2"><Phone className="w-4 h-4 mt-0.5 text-[var(--brand-gold)]" /><a href={BRAND.phoneHref} className="hover:text-white">{BRAND.phone}</a></li>
            <li className="flex items-start gap-2"><Mail className="w-4 h-4 mt-0.5 text-[var(--brand-gold)]" /><a href={`mailto:${BRAND.email}`} className="hover:text-white">{BRAND.email}</a></li>
            <li className="flex items-start gap-2"><MapPin className="w-4 h-4 mt-0.5 text-[var(--brand-gold)]" /><span>{BRAND.address}</span></li>
            <li className="flex items-start gap-2"><Clock className="w-4 h-4 mt-0.5 text-[var(--brand-gold)]" />
              <div>
                {BRAND.hours.map(h => <div key={h.day}>{h.day}: {h.time}</div>)}
              </div>
            </li>
          </ul>
        </div>

        <div>
          <h4 className="font-bold mb-4 text-[var(--brand-gold)] uppercase text-xs tracking-widest" style={{ fontFamily: "Outfit" }}>Trust & Security</h4>
          <ul className="space-y-3 text-sm text-white/80">
            <li className="flex items-center gap-2"><Lock className="w-4 h-4 text-[var(--brand-gold)]" /> 256-Bit SSL Encryption</li>
            <li className="flex items-center gap-2"><Shield className="w-4 h-4 text-[var(--brand-gold)]" /> Privacy Guaranteed</li>
            <li className="flex items-center gap-2"><BadgeCheck className="w-4 h-4 text-[var(--brand-gold)]" /> Secure {PROVIDER_LABEL} Checkout</li>
          </ul>
          <div className="mt-5">
            <ThumbtackBadge variant="footer" />
          </div>
          <div className="mt-6 p-4 rounded-xl bg-white/5 border border-white/10 text-xs text-white/70 leading-relaxed">
            <strong className="text-[var(--brand-gold)]">Instant Online Booking</strong> is a convenient way to schedule cleaning within 20 miles of Evansville.
            <br />For handyman work or cleanings beyond that radius, we're happy to provide a personalized quote — just <Link to="/request-quote" className="text-white underline hover:text-[var(--brand-gold)]">request one</Link> or call <a href={BRAND.phoneHref} className="text-white hover:text-[var(--brand-gold)] font-semibold">{BRAND.phone}</a>.
          </div>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-6 flex flex-col md:flex-row items-center justify-between text-xs text-white/50 gap-3">
          <span>© {new Date().getFullYear()} {BRAND.legalName}. All rights reserved.</span>
          <div className="flex items-center gap-5">
            <Link to="/terms" className="hover:text-[var(--brand-gold)] transition" data-testid="footer-terms">Terms & Conditions</Link>
            <Link to="/privacy" className="hover:text-[var(--brand-gold)] transition" data-testid="footer-privacy">Privacy Policy</Link>
            <span>We do NOT offer plumbing or electrical services.</span>
            <Link to="/admin/login" className="text-white/30 hover:text-[var(--brand-gold)]/70 transition text-[10px] uppercase tracking-wider" data-testid="footer-internal">
              Internal
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
